// 보스 '고목 수호자': 골격 텍스처 재생. 애니메이션 GLB 실패 때 기존 절차 변형으로 대신한다.
// 자세는 CPU가 상태별 목표값을 만들고 지수 감쇠로 따라가며, uniform 몇 개만 바꾼다(프레임 루프 할당 없음).
// 가슴 녹색 결정은 텍스처의 녹색 채도로 뽑아 발광시킨다(블룸 문턱 0.95를 넘기도록). 무적은 금빛 림 맥동(1.4Hz, 화면 섬광 아님).
// 영웅이 가려지면 1px 체크무늬 디더로 비춘다. GLB를 못 읽으면 절차 그레이박스(actors/bossGraybox.ts)로 대신한다.
import * as THREE from 'three/webgpu';
import {
  attribute, cameraPosition, cos, float, floor, materialEmissive, mix, modelWorldMatrixInverse, normalize, normalLocal, positionGeometry,
  positionViewDirection, screenCoordinate, sin, smoothstep, step, texture, transformNormalToView, uniform, vec3, vec4,
} from 'three/tsl';
import { BOSS, BOSS_TIMING } from '../shared/config';
import { BOSS_DEFS, BOSS_KEYS, type BossDef } from '../shared/content/bosses';
import { BossKind, BossState, type GameView } from '../shared/types';
import { RAGE_COLORS } from '../vfx/bossRageFx';
import { loadAnimatedActor } from './actors/gltf';
import { bossLookMaterial, EMPTY_ATTACH, loadAttachments, lookOfRegion, REGION_LOOKS, type RegionLook } from './actors/regionAttach';
import { BOSS_CLIPS, bossFrame, PoseBlend } from './actors/animation';
import { skinNodes, type SkinAtlas } from './actors/skinning';
import { EmberBossVisual } from './actors/emberBoss';
import { WyvernVisual } from './actors/wyvern';
import { BossGraybox } from './actors/bossGraybox';
import { toonGradient } from './actors/toon';
import { cameraYaw, inCameraCorridor } from '../shared/cameraYaw';
import type { GameClocks } from './clock';
import { RimControl } from './materials';
import { contactMaterial } from './env/fx';
import { TURN_BOSS, TURN_BOSS_COMBAT, turnToward } from './actors/turn';
import { clamp01, damp, easeIn, easeInOut, easeOut, lerp, phaseOf } from './util';

type N = any; // eslint-disable-line @typescript-eslint/no-explicit-any

/** 상태별 연출 길이(틱). 게임 쪽 타이밍과 다르면 clamp되어 마지막 자세를 유지한다. */
const T = { ...BOSS_TIMING, slam: BOSS_TIMING.strike, sweep: BOSS_TIMING.strike } as const;
/** 모델 높이(m)와 어깨 피벗 */
const SHOULDER_X = 2.0;
const SHOULDER_Y = 4.9;
const SHOULDER_Z = 0.0;
/** 외곽선 두께(월드 m). 카메라 14m, 720p에서 약 3px */
const OUTLINE_M = 0.04;
/** 렌더 크기 배율(모델 높이 7m → 약 8.4m). 판정 반경은 바꾸지 않는다 */
const BOSS_SCALE = 1.2;

export class BossVisual {
  /** 품질 low면 레이드 비룡 노멀맵을 끈다(index.ts가 만든 직후 정한다) */
  lowQuality = false;
  readonly root = new THREE.Group();
  readonly rim = new RimControl(0xfff0c8, 0.3);
  ready = false;
  x = 0; z = 0;

  private readonly ember = new EmberBossVisual();
  /** 레이드 '잿불 비룡'(BossKind.Wyvern): 처음 비룡을 그릴 때(레이드 진입) 만들고 전용 GLB를 그때 읽는다. 읽는 동안·실패하면 절차 대체판을 그린다(만들다 실패해도 다른 보스·부팅은 그대로) */
  private wyvern: WyvernVisual | null = null;
  private wyvernFailed = false;
  private wyvernCone = true;
  private gray: BossGraybox | null = null;
  private body: THREE.Mesh | null = null;
  private hull: THREE.Mesh | null = null;
  private loading: Promise<void> | null = null;
  private coreLoading: Promise<void> | null = null;
  private emberLoading: Promise<void> | null = null;
  private readonly mats: THREE.Material[] = [];
  private bodyMat: THREE.Material | null = null;
  private hullMat: THREE.Material | null = null;
  private geo: THREE.BufferGeometry | null = null;
  /** 발밑 접촉 그림자(낮은 품질에서 그림자가 꺼져도 거대한 몸이 바닥에 붙어 보이게; 블라인드 감사 P1 '떠 있거나 묻혀 보임') */
  private contact: THREE.Mesh | null = null;
  private skin: SkinAtlas | undefined;
  private readonly pose = uniform(new THREE.Vector3());
  private readonly blend = new PoseBlend(1);
  private cycle = 0;
  /** 그리는 몸 방향(rad, turn.ts). NaN이면 다음 프레임에 게임 방향으로 맞춘다 */
  private bodyYaw = Number.NaN;

  // 셰이더 uniform
  private readonly uLean = uniform(0);
  private readonly uTwist = uniform(0);
  private readonly uSy = uniform(1);
  private readonly uPulse = uniform(1);
  private readonly uArmL = uniform(new THREE.Vector2(0.15, -0.2));
  private readonly uArmR = uniform(new THREE.Vector2(0.15, 0.2));
  private readonly uGlow = uniform(0.6);
  /** D3a 보스 변형 색조(원형 고목 수호자 모델을 고블린 족장·용암 골렘·묘지기 기사·늪의 히드라로) */
  private readonly uTint = uniform(new THREE.Color(1, 1, 1));
  private variant = -1;
  private def: BossDef = BOSS_DEFS.treant;
  private readonly uHit = uniform(0);
  /** c021 지역 변형(묘지기 기사 = 서리, 늪의 히드라 = 독): 같은 재질을 쓰는 조각 붙은 메시와 조각 발광 값 */
  private readonly lookMesh: Partial<Record<RegionLook, { body: THREE.Mesh; hull: THREE.Mesh }>> = {};
  private readonly lookLoading: Partial<Record<RegionLook, Promise<void>>> = {};
  private look: RegionLook | null = null;
  /** QA 전후 비교: 켜면 지역 변형 메시 대신 원형 색조판(근접형·시전형 모두) */
  legacyLook = false;
  private readonly uAttGain = uniform(1.5);
  private readonly uAttPulse = uniform(0);
  private readonly uSparkle = uniform(new THREE.Color(0, 0, 0));
  private readonly uCrystal = uniform(new THREE.Color(0.45, 1.0, 0.4));
  /** 영웅이 보스 뒤에 있을 때 보스를 1px 체크무늬로 비춰 보이게 하는 정도 0..1 */
  private readonly fade = uniform(0);
  private fadeV = 0;

  // CPU 쪽 부드러운 상태
  private armLX = 0; private armRX = 0; private armLZ = 0; private armRZ = 0;
  private yawOff = 0;
  private leanX = 0;
  private yOff = 0;
  private sy = 1;
  private pulse = 1;
  private glow = 0.6;
  private twist = 0;

  /** 비룡 공중 높이(m). 그 밖의 보스는 0 */
  get height(): number { return this.wyvern && this.wyvern.root.visible ? this.wyvern.height : 0; }

  /** 비룡 입 위치·방향(월드). 비룡이 없거나 숨었으면 false — VFX 레인 브레스가 쓴다(입 뼈 기준) */
  wyvernMouth(pos: THREE.Vector3, dir?: THREE.Vector3): boolean {
    const w = this.wyvern;
    if (!w || !w.mouthWorld(pos)) return false;
    if (dir) w.mouthDir(dir);
    return true;
  }
  /** 비룡 몸에 붙은 임시 브레스 원뿔(기본 켬). raidFx 쪽 브레스가 생기면 끈다 */
  get wyvernBreathCone(): boolean { return this.wyvernCone; }
  set wyvernBreathCone(on: boolean) { this.wyvernCone = on; if (this.wyvern) this.wyvern.breathCone = on; }

  private ensureWyvern(): WyvernVisual | null {
    if (this.wyvern || this.wyvernFailed) return this.wyvern;
    try { this.wyvern = new WyvernVisual({ normalMap: !this.lowQuality }); this.wyvern.breathCone = this.wyvernCone; this.root.add(this.wyvern.root); }
    catch (err) { this.wyvernFailed = true; console.warn('비룡 모델을 만들지 못함', err); }
    return this.wyvern;
  }

  load(): Promise<void> {
    if (!this.loading) this.loading = Promise.all([this.loadCore(), this.loadEmber()]).then(() => undefined);
    return this.loading;
  }

  /** 고목 수호자(R1 보스)만 읽는다. 첫 화면(마을)에는 필요 없어 준비 뒤 뒤에서 부른다. */
  loadCore(): Promise<void> {
    if (!this.coreLoading) this.coreLoading = this.build();
    return this.coreLoading;
  }

  /** R2 잿불 보스 전용 모델. 끝나기 전에는 잿불 보스가 보이지 않는다(R2 입장은 R1 클리어 뒤라 그 전에 끝난다). */
  loadEmber(): Promise<void> {
    if (!this.emberLoading) this.emberLoading = this.ember.load().catch((err: unknown) => console.warn('잿불 보스 모델을 읽지 못함', err));
    return this.emberLoading;
  }

  /**
   * c021 지역 변형 보스 모델: 근접형(고목 원형)은 여기서, 시전형(잿불 원형)은 EmberBossVisual이 조각을 붙인다.
   * 원형과 같은 골격·클립이라 원형 재질(골격 텍스처)을 그대로 쓰고 지오메트리만 다르다. 실패하면 원형 색조판으로 그린다.
   */
  loadLook(look: RegionLook): Promise<void> {
    let p = this.lookLoading[look];
    if (!p) {
      p = Promise.all([this.loadCore().then(() => this.buildLook(look)), this.loadEmber().then(() => this.ember.loadLook(look))])
        .then(() => undefined).catch((err: unknown) => console.warn('지역 변형 보스 준비 실패: ' + look, err));
      this.lookLoading[look] = p;
    }
    return p;
  }

  /** 지역 변형 보스 메시가 준비됐는지(QA): 근접형·시전형 */
  lookReady(look: RegionLook): { treant: boolean; ember: boolean } {
    return { treant: !!this.lookMesh[look], ember: this.ember.lookReady(look) };
  }

  private async buildLook(look: RegionLook): Promise<void> {
    if (!this.body || !this.hull || !this.bodyMat || !this.hullMat || !this.skin || new URLSearchParams(location.search).has('r34legacy')) return;
    const parts = await loadAttachments('boss_treant', look);
    if (!parts) return;
    const a = await loadAnimatedActor('boss_treant', BOSS_CLIPS, parts);
    if (!a?.skin || !a.geometry.hasAttribute('aAtt')) { a?.geometry.dispose(); a?.skin?.texture.dispose(); return; }
    // 골격 텍스처는 원형 것을 쓴다(같은 본 순서·클립)
    a.skin.texture.dispose();
    const body = new THREE.Mesh(a.geometry, this.bodyMat);
    body.castShadow = true; body.frustumCulled = false; body.scale.setScalar(BOSS_SCALE); body.visible = false;
    const hull = new THREE.Mesh(a.geometry, this.hullMat);
    hull.frustumCulled = false; hull.scale.setScalar(BOSS_SCALE); hull.visible = false;
    this.root.add(body, hull);
    this.lookMesh[look] = { body, hull };
  }

  private async build(): Promise<void> {
    // 빈 조각 묶음: 원형에도 aAtt(0)를 달아 지역 변형 메시와 같은 재질을 쓴다
    const asset = await loadAnimatedActor('boss_treant', BOSS_CLIPS, EMPTY_ATTACH);
    this.root.add(this.ember.root);
    if (!asset) {
      this.gray = new BossGraybox();
      this.root.add(this.gray.root);
      this.root.visible = false;
      this.ready = true;
      return;
    }
    const geometry = asset.geometry;
    this.geo = geometry;
    this.skin = asset.skin;

    // ── 정점 변형 ───────────────────────────────────────
    const deform = (thick: number): { pos: N; nrm: N } => {
      const p = positionGeometry;
      // 조명·외곽선 법선: 정점 법선과 이웃 평균 매끈 법선(aSmooth)을 섞는다(구겨진 감면 메시의 툰 조각 완화)
      const n = normalize(normalLocal.mul(0.4).add(attribute('aSmooth', 'vec3').mul(0.6)));
      if (asset.skin) {
        const sk = skinNodes(asset.skin, this.pose, p, n, attribute('aSmooth', 'vec3'));
        if (thick <= 0) return { pos: sk.position, nrm: sk.normal };
        const inflated = sk.position.add(sk.smooth.mul(thick / BOSS_SCALE));
        const camLocal = modelWorldMatrixInverse.mul(vec4(cameraPosition, 1)).xyz;
        return { pos: inflated.add(normalize(inflated.sub(camLocal)).mul(0.35 / BOSS_SCALE)), nrm: sk.normal };
      }
      const px = p.x, py = p.y, pz = p.z;
      // 팔: 어깨 피벗 주위로 롤(Z) 다음 피치(X). 좌우는 x 부호로 가른다.
      // 팔 영역(정면 정점 분포 실측): 안쪽 경계가 아래팔·발톱 |x|>2.2, 위팔·어깨 |x|>1.65.
      // 뿌리(y<0.6)와 머리 쪽(y>5.3)은 뺀다. 경계를 가로지르는 삼각형이 늘어나므로 팔 회전은 0.7rad 이하로 두고`n      // 경계를 넓게 잡아 휘는 느낌으로 보이게 한다. 들어 올리는 큰 동작은 전신 젖힘·늘림이 맡는다(브리프 3).
      const xIn = mix(float(2.2), float(1.65), smoothstep(2.5, 4.0, py));
      const armW = smoothstep(xIn.sub(0.4), xIn.add(0.4), px.abs()).mul(smoothstep(0.6, 1.0, py)).mul(float(1).sub(smoothstep(5.2, 5.6, py)));
      const right = step(0, px);
      const pitch = mix(this.uArmL.x, this.uArmR.x, right);
      const roll = mix(this.uArmL.y, this.uArmR.y, right);
      const shx = mix(float(-SHOULDER_X), float(SHOULDER_X), right);
      const dx = px.sub(shx), dy0 = py.sub(SHOULDER_Y), dz0 = pz.sub(SHOULDER_Z);
      const cr = cos(roll), sr = sin(roll);
      const cp = cos(pitch), sp = sin(pitch);
      const rx = dx.mul(cr).sub(dy0.mul(sr));
      const ry = dx.mul(sr).add(dy0.mul(cr));
      const arm = vec3(rx.add(shx), ry.mul(cp).sub(dz0.mul(sp)).add(SHOULDER_Y), dz0.mul(cp).add(ry.mul(sp)).add(SHOULDER_Z));
      const q0 = mix(p, arm, armW);
      const nrx = n.x.mul(cr).sub(n.y.mul(sr));
      const nry = n.x.mul(sr).add(n.y.mul(cr));
      const nArm = vec3(nrx, nry.mul(cp).sub(n.z.mul(sp)), n.z.mul(cp).add(nry.mul(sp)));
      const n0 = mix(n, nArm, armW);
      // 몸통 비틀기(높이 가중)
      const tw = this.uTwist.mul(smoothstep(1.2, 5.2, q0.y));
      const ct = cos(tw), st = sin(tw);
      const q1 = vec3(q0.x.mul(ct).add(q0.z.mul(st)), q0.y, q0.z.mul(ct).sub(q0.x.mul(st)));
      const n1 = vec3(n0.x.mul(ct).add(n0.z.mul(st)), n0.y, n0.z.mul(ct).sub(n0.x.mul(st)));
      // 허리(y=1.0)를 피벗으로 앞뒤 기울임(높이 가중) + 상하 눌림 + 펄스
      const lw = smoothstep(0.4, 5.5, q1.y);
      const th = this.uLean.mul(lw);
      const cl = cos(th), sl = sin(th);
      const dy = q1.y.sub(1.0);
      const q2 = vec3(q1.x, float(1.0).add(dy.mul(cl)).sub(q1.z.mul(sl)), q1.z.mul(cl).add(dy.mul(sl)));
      const n2 = normalize(vec3(n1.x, n1.y.mul(cl).sub(n1.z.mul(sl)), n1.z.mul(cl).add(n1.y.mul(sl))));
      const q3 = vec3(q2.x, q2.y.mul(this.uSy), q2.z).mul(this.uPulse);
      if (thick <= 0) return { pos: q3, nrm: n2 };
      // 외곽선: 법선 방향으로 부풀린 뒤 시선 방향으로 뒤로 밀어(화면 위치 유지) 구겨진 면을 뚫고 나오지 않게 한다
      const inflated = q3.add(n2.mul(thick / BOSS_SCALE));
      const camLocal = modelWorldMatrixInverse.mul(vec4(cameraPosition, 1)).xyz;
      return { pos: inflated.add(normalize(inflated.sub(camLocal)).mul(0.35 / BOSS_SCALE)), nrm: n2 };
    };

    // ── 본체: 툰 2단 + 림 + 결정 발광 ────────────────────
    const bd = deform(0);
    const body = new THREE.MeshToonNodeMaterial({ gradientMap: toonGradient() });
    body.positionNode = bd.pos;
    const nView = normalize(transformNormalToView(bd.nrm)).toVarying('bossNV');
    body.normalNode = nView;
    const tex = asset.map ? texture(asset.map).rgb : vec3(0.5, 0.45, 0.35);
    // 매끈한 칠 색(aPaint) 위에 원래 텍스처를 섞는다(몹과 같은 방식, 보스는 크게 보여 세부 비율을 높인다)
    // 지역 조각(aAtt.w ≥ 0.5)은 몸 색조를 받지 않고 자기 정점색(actors/regionAttach.ts)
    const att = asset.skin ? attribute('aAtt', 'vec4') : vec4(0);
    const attMask = step(0.5, att.w);
    body.colorNode = mix((asset.paint ? mix(attribute('aPaint', 'vec3'), tex, 0.55) : tex).mul(this.uTint), att.xyz, attMask);
    const fres = float(1).sub(nView.dot(positionViewDirection).clamp(0, 1)).pow(3);
    // 가슴 결정: 텍스처의 녹색 우세도 + 가슴 영역(모델 공간)만. 잎의 어두운 녹색은 문턱 아래다.
    const gdiff = tex.g.sub(tex.r.mul(0.85).max(tex.b.mul(1.2)));
    const rest = asset.skin ? attribute('aActorRest', 'vec3') : positionGeometry;
    const region = smoothstep(3.0, 3.5, rest.y).mul(smoothstep(5.2, 4.7, rest.y)).mul(smoothstep(1.1, 0.7, rest.x.abs())).mul(step(-0.2, rest.z));
    const crystal = smoothstep(0.17, 0.3, gdiff).mul(region).mul(float(1).sub(attMask));
    const attGlow = att.w.sub(1).max(0).mul(attMask);
    const pulse = float(1).sub(this.uAttPulse.mul(0.5)).add(sin(this.uGlow.mul(2.2).add(rest.y.mul(1.3))).mul(this.uAttPulse.mul(0.5)));
    (body as unknown as { emissiveNode: unknown }).emissiveNode = materialEmissive
      .add(this.rim.color.mul(fres).mul(this.rim.strength))
      .add((this.uCrystal as N).mul(tex.mul(0.55).add(0.45)).mul(crystal).mul(this.uGlow))
      // 지역 조각 발광(결정 끝·독샘) + 조각 가장자리 반짝임. 보스 숨(uGlow)에 맞춰 함께 밝아진다
      .add(att.xyz.mul(attGlow.mul(this.uAttGain)).mul(pulse).mul(this.uGlow.mul(0.35).add(0.75)))
      .add((this.uSparkle as N).mul(fres.mul(attMask).mul(0.55)))
      // 피격: 몸 전체 발광 대신 가장자리만 흰빛(블룸과 겹쳐 몸이 하얗게 날아가지 않게, 몹과 같은 규칙)
      .add(vec3(1.0, 0.97, 0.88).mul(fres.mul(1.1)).mul(this.uHit));
    body.emissive = new THREE.Color(0x000000);
    // 디더 반투명: 불투명 파이프라인 그대로 픽셀 절반을 버린다(정렬 문제 없음)
    const checker = floor(screenCoordinate.x).add(floor(screenCoordinate.y)).mod(2);
    body.opacityNode = mix(float(1), checker, this.fade);
    body.alphaTest = 0.5;

    const hullMat = new THREE.MeshBasicNodeMaterial({ color: 0x0b0f1c, side: THREE.BackSide });
    hullMat.positionNode = deform(OUTLINE_M).pos;
    hullMat.opacityNode = mix(float(1), checker, this.fade);
    hullMat.alphaTest = 0.5;
    this.mats.push(body, hullMat);

    const mesh = new THREE.Mesh(geometry, body);
    mesh.castShadow = true;
    mesh.frustumCulled = false;
    const hull = new THREE.Mesh(geometry, hullMat);
    hull.frustumCulled = false;
    hull.scale.setScalar(BOSS_SCALE);
    mesh.scale.setScalar(BOSS_SCALE);
    this.body = mesh;
    this.hull = hull;
    this.root.add(mesh, hull);
    // 접촉 그림자: 몸 반폭(rest)의 약 2.3배 지름 원판. 루트 배율(보스 변형 크기)을 같이 따른다.
    const contactGeo = new THREE.PlaneGeometry(1, 1); contactGeo.rotateX(-Math.PI / 2);
    const contactMat = contactMaterial(0.6);
    const contact = new THREE.Mesh(contactGeo, contactMat);
    contact.scale.setScalar(Math.min(9, Math.max(2, asset.halfWidth * BOSS_SCALE * 2.3)));
    contact.position.y = 0.03; contact.renderOrder = 2; contact.frustumCulled = false;
    this.root.add(contact);
    this.contact = contact;
    this.mats.push(contactMat);
    this.bodyMat = body; this.hullMat = hullMat;
    this.root.visible = false;
    if (new URLSearchParams(location.search).has('qa')) (window as unknown as { __DHBOSS__: BossVisual }).__DHBOSS__ = this;
    this.ready = true;
  }

  update(view: GameView, alpha: number, dt: number, clocks: GameClocks): void {
    const b = view.boss;
    if (b.kind === BossKind.Wyvern) { this.updateWyvern(view, alpha, dt, clocks); return; }
    if (this.wyvern) this.wyvern.root.visible = false;
    if (!this.ready) { this.root.visible = false; return; }
    const st = b.state;
    const isEmber = b.kind === BossKind.Ember;
    // 보스 변형(D3a): 정의가 바뀔 때만 색조·크기를 맞춘다(원형 모델 공유)
    const vi = typeof b.variant === 'number' ? b.variant : 0;
    if (vi !== this.variant) {
      this.variant = vi;
      this.def = BOSS_DEFS[BOSS_KEYS[vi] ?? 'treant'] ?? BOSS_DEFS.treant;
      (this.uTint.value as THREE.Color).setHex(this.def.tint);
      this.root.scale.setScalar(isEmber ? 1 : this.def.scale);
      this.look = lookOfRegion(this.def.region);
      const lk = this.look;
      const lm = bossLookMaterial(lk);
      this.uAttGain.value = lm.gain;
      this.uAttPulse.value = lm.pulse;
      (this.uSparkle.value as THREE.Color).setRGB(lm.sparkle[0], lm.sparkle[1], lm.sparkle[2]);
      // 가슴 결정 발광: 서리 청백·모래 청록·심연 보라, 그 밖은 원래 녹색
      (this.uCrystal.value as THREE.Color).setRGB(lm.crystal[0], lm.crystal[1], lm.crystal[2]);
      this.ember.setVariant(this.def.tint, isEmber ? this.def.scale : 1, lk);
    }
    this.ember.root.visible = isEmber;
    // 근접형: 지역 변형 메시가 준비됐으면 그것만, 아니면 원형(색조판)
    const lm = this.look && !this.legacyLook ? this.lookMesh[this.look] : undefined;
    if (this.body) this.body.visible = !isEmber && !lm;
    if (this.hull) this.hull.visible = !isEmber && !lm;
    for (let i = 0; i < REGION_LOOKS.length; i++) {
      const m = this.lookMesh[REGION_LOOKS[i]];
      if (m) m.body.visible = m.hull.visible = !isEmber && m === lm;
    }
    this.ember.setLegacy(this.legacyLook);
    if (this.gray) this.gray.root.visible = !isEmber;
    if (isEmber) {
      this.root.visible = st !== BossState.Inactive;
      this.root.position.set(0, 0, 0); this.root.rotation.set(0, 0, 0);
      if (!this.root.visible) { this.ember.reset(); return; }
      this.ember.update(view, alpha, clocks);
      this.x = this.ember.root.position.x; this.z = this.ember.root.position.z;
      return;
    }
    this.ember.reset();
    const show = st !== BossState.Inactive && st !== BossState.Dead;
    if (this.gray) {
      this.gray.update(view, alpha, dt, clocks);
      this.root.visible = show;
      this.x = this.gray.x; this.z = this.gray.z;
      return;
    }
    this.root.visible = show;
    if (!show) { this.blend.reset(); this.cycle = 0; this.bodyYaw = Number.NaN; return; }
    this.x = b.prevX + (b.x - b.prevX) * alpha;
    this.z = b.prevZ + (b.z - b.prevZ) * alpha;
    const t = phaseOf(b.stateTick, clocks.worldRate, alpha);
    const time = clocks.worldNow;

    let aLX = 0.12, aRX = 0.12, aLZ = -0.12, aRZ = 0.12; // 팔 아래로 늘어짐
    let yaw = 0, lean = 0, yOff = 0, sy = 1, pulse = 1, twist = 0;
    let glow = 0.7 + 0.18 * Math.sin(time * 0.05);
    let sharp = 8;
    switch (st) {
      case BossState.Intro: {
        const p = clamp01(t / T.intro);
        yOff = -8.6 * (1 - easeOut(p)); // 땅에서 솟아오른다
        aLX = aRX = lerp(0.9, 0.12, easeInOut(p));
        pulse = 1 + 0.03 * Math.sin(p * Math.PI);
        glow = 0.3 + 1.0 * easeIn(p);
        sharp = 0;
        break;
      }
      case BossState.Chase: {
        const ph = time * 0.12;
        lean = 0.06; yOff = Math.abs(Math.sin(ph)) * 0.18;
        aLX = 0.3 + Math.sin(ph) * 0.35; aRX = 0.3 - Math.sin(ph) * 0.35;
        twist = Math.sin(ph) * 0.06;
        break;
      }
      case BossState.SlamWindup: {
        const p = easeOut(clamp01(t / T.slamWindup));
        aLX = aRX = lerp(0.12, -0.7, p); // 팔을 앞위로 치켜든다
        aLZ = -0.3 * p; aRZ = 0.3 * p;
        lean = -0.3 * p; sy = 1 + 0.08 * p; // 전신을 뒤로 젖히고 늘인다
        glow = 0.8 + 0.9 * p;
        sharp = 0;
        break;
      }
      case BossState.Slam: {
        const p = clamp01(t / T.slam);
        aLX = aRX = lerp(-0.7, 0.4, easeIn(Math.min(1, p * 2.2)));
        lean = 0.4 * Math.min(1, p * 3) * (1 - clamp01((p - 0.6) / 0.4) * 0.6);
        sy = 1 - 0.08 * (p < 0.5 ? p * 2 : 2 - p * 2); // 착지 순간 눌림
        glow = 1.7 - 0.8 * p;
        sharp = 0;
        break;
      }
      case BossState.SweepWindup: {
        const p = easeOut(clamp01(t / T.sweepWindup));
        aLZ = lerp(-0.12, -0.45, p); aRZ = lerp(0.12, 0.45, p);
        aLX = aRX = lerp(0.12, 0.3, p);
        yaw = 0; twist = -0.9 * p; // 상체를 크게 틀어 감는다
        lean = -0.06 * p;
        glow = 0.8 + 0.6 * p;
        sharp = 0;
        break;
      }
      case BossState.Sweep: {
        const p = clamp01(t / T.sweep);
        aLZ = -0.45; aRZ = 0.45; aLX = aRX = 0.3;
        twist = lerp(-0.9, 0.9, easeOut(p));
        lean = 0.1;
        glow = 1.4 - 0.5 * p;
        sharp = 0;
        break;
      }
      case BossState.Summon: {
        const p = clamp01(t / T.summon);
        aLX = aRX = lerp(0.12, -0.6, easeOut(Math.min(1, p * 3)));
        aLZ = -0.45; aRZ = 0.45;
        lean = -0.12 * easeOut(Math.min(1, p * 3));
        pulse = 1 + 0.045 * Math.max(0, Math.sin(t * 0.42)); // 포효 펄스
        glow = 1.3 + 0.9 * Math.max(0, Math.sin(t * 0.42));
        break;
      }
      case BossState.Stagger:
        lean = -0.28 + Math.sin(t * 0.6) * 0.04;
        twist = Math.sin(t * 0.45) * 0.1;
        aLX = aRX = 0.45; aLZ = -0.35; aRZ = 0.35;
        glow = 0.25 + 0.25 * Math.abs(Math.sin(t * 0.5));
        sharp = 14;
        break;
      case BossState.Dying: {
        const p = clamp01(t / T.dying);
        lean = -1.35 * easeIn(p);
        yOff = -1.9 * p;
        aLX = aRX = 0.5; aLZ = -0.4; aRZ = 0.4;
        glow = 1.4 * (1 - p);
        sharp = 0;
        break;
      }
      default:
        break;
    }
    const k = sharp === 0 ? 1 : damp(sharp, dt);
    this.armLX += (aLX - this.armLX) * k; this.armRX += (aRX - this.armRX) * k;
    this.armLZ += (aLZ - this.armLZ) * k; this.armRZ += (aRZ - this.armRZ) * k;
    this.yawOff += (yaw - this.yawOff) * k;
    this.leanX += (lean - this.leanX) * k;
    this.twist += (twist - this.twist) * k;
    this.yOff += (yOff - this.yOff) * k;
    this.sy += (sy - this.sy) * k;
    this.pulse += (pulse - this.pulse) * k;
    this.glow += (glow - this.glow) * (sharp === 0 ? 1 : damp(10, dt));

    if (this.skin) {
      const velocity = clocks.worldRate > 0 ? Math.hypot(b.x - b.prevX, b.z - b.prevZ) * 60 / clocks.worldRate : 0;
      const playback = st === BossState.Chase ? velocity / Math.max(0.1, this.skin.clips.walk.speed * BOSS_SCALE) * (this.skin.clips.walk.direction ?? 1) : 1;
      this.cycle += clocks.worldDelta / 60 * playback;
      this.blend.update(0, st, bossFrame(this.skin, st, t, this.cycle), time, st === BossState.Slam || st === BossState.Sweep);
      this.pose.value.set(this.blend.a[0], this.blend.b[0], this.blend.mix[0]);
    }
    this.root.position.set(this.x, this.skin ? 0 : this.yOff, this.z);
    // 게임 facing은 추격·공격 준비 시작에 한 틱으로 돌아간다 → 거대한 몸은 각속도 상한으로 돈다(등장 연출은 게임 방향 그대로)
    if (Number.isNaN(this.bodyYaw) || st === BossState.Intro) this.bodyYaw = b.facing;
    else this.bodyYaw = turnToward(this.bodyYaw, b.facing, clocks.worldDelta / 60, st === BossState.Idle || st === BossState.Chase ? TURN_BOSS : TURN_BOSS_COMBAT);
    this.root.rotation.y = this.bodyYaw + (this.skin ? 0 : this.yawOff);
    this.uLean.value = this.leanX;
    this.uTwist.value = this.twist;
    this.uSy.value = this.sy;
    this.uPulse.value = this.pulse;
    this.uArmL.value.set(this.armLX, this.armLZ);
    this.uArmR.value.set(this.armRX, this.armRZ);
    this.uGlow.value = this.glow;

    // 영웅이 보스 북쪽(카메라 반대편)에 있어 몸에 실제로 가릴 때만 보스를 비춰 보이게 한다.
    // 카메라 피치 50°에서 키 8.4m 보스는 북쪽 약 7m까지 가린다. 앞(남쪽)이나 옆의 근접전은 비추지 않는다(영웅 x-ray가 따로 있다).
    const h = view.hero;
    const hx = h.prevX + (h.x - h.prevX) * alpha, hz = h.prevZ + (h.z - h.prevZ) * alpha;
    // "북쪽"은 카메라 기준이다: 사용자 회전(yaw)을 따라 영웅~카메라 통로(카메라 쪽 +(sin, cos))에 보스가 있을 때만 비춘다(월드 z 고정이면 카메라를 돌렸을 때 틀린다)
    const behind = inCameraCorridor(this.x - hx, this.z - hz, cameraYaw.rad, 0.6, 8, 4.2) ? 1 : 0;
    this.fadeV += (behind - this.fadeV) * damp(10, dt);
    this.fade.value = this.fadeV > 0.02 ? this.fadeV : 0;

    // 피격 플래시(약하게: 연속 타격에서 블룸과 겹쳐 하얗게 날아가지 않도록), 무적이면 금빛 림 맥동
    this.uHit.value = b.hitTick < 4 ? 1 : 0;
    if (b.invulnerable === true) {
      this.rim.color.value.setHex(0xffd36a);
      this.rim.strength.value = 0.9 + 0.5 * Math.sin(clocks.realMs * 0.0088);
    } else {
      // S5 분노(붉은 주황)·광폭(보라)은 그 색 가장자리로 더 세게, 천천히(0.6Hz) 맥동한다(점멸 없음)
      const rage = (b.rage ?? 0) | 0;
      if (rage > 0) {
        this.rim.color.value.setHex(rage === 2 ? RAGE_COLORS[2].edge : RAGE_COLORS[1].core);
        this.rim.strength.value = 0.95 + 0.2 * Math.sin(clocks.realMs * 0.0038);
      } else {
        // 페이즈 2(D3a 지역보스)는 정의의 가장자리 색으로 더 세게 빛난다
        this.rim.color.value.setHex(b.phase2 ? this.def.rim : this.variant > 0 ? this.def.rim : 0xfff0c8);
        this.rim.strength.value = b.phase2 ? 0.75 + 0.15 * Math.sin(clocks.realMs * 0.004) : this.variant > 0 ? 0.45 : 0.3;
      }
    }
  }

  /** 문서 대조용: 설계 반경 */
  static readonly radius = BOSS.radius;

  /** 레이드 비룡: 다른 보스 메시를 모두 숨기고 비룡만 그린다 */
  private updateWyvern(view: GameView, alpha: number, dt: number, clocks: GameClocks): void {
    const st = view.boss.state;
    this.root.visible = st !== BossState.Inactive && st !== BossState.Dead;
    this.root.position.set(0, 0, 0); this.root.rotation.set(0, 0, 0); this.root.scale.setScalar(1);
    this.variant = -1;
    this.ember.root.visible = false;
    if (this.body) this.body.visible = false;
    if (this.hull) this.hull.visible = false;
    for (let i = 0; i < REGION_LOOKS.length; i++) { const m = this.lookMesh[REGION_LOOKS[i]]; if (m) m.body.visible = m.hull.visible = false; }
    if (this.gray) this.gray.root.visible = false;
    const w = this.ensureWyvern();
    if (!w) { this.x = view.boss.x; this.z = view.boss.z; return; }
    w.update(view, alpha, dt, clocks);
    this.x = w.x; this.z = w.z;
  }

  dispose(): void {
    this.wyvern?.dispose();
    this.ember.dispose();
    this.gray?.dispose();
    this.geo?.dispose();
    this.contact?.geometry.dispose();
    for (const k of REGION_LOOKS) this.lookMesh[k]?.body.geometry.dispose();
    this.skin?.texture.dispose();
    for (const m of this.mats) m.dispose();
    this.body = null; this.hull = null;
  }
}
